import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import {
  getCurrentForm,
  getAllForms,
  getForm,
  createForm,
  updateForm,
  getFormTemplates,
  createFormTemplate,
  deleteFormTemplate,
} from '@/services/api/form.service'

import { useWorkspace } from './useWorkspace'

/** @param options.enabled set false to skip loading (e.g. the user may not view the form) */
export function useForm(recordGroupId: string, options: { enabled?: boolean } = {}) {
  const { currentWorkspaceId } = useWorkspace()
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['workspace', currentWorkspaceId, 'record-groups', recordGroupId, 'current-form'],
    queryFn: () => getCurrentForm(recordGroupId),
    enabled: !!recordGroupId && !!currentWorkspaceId && options.enabled !== false,
    staleTime: 60 * 1000,
  })

  const createMutation = useMutation({
    mutationFn: createForm,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['workspace', currentWorkspaceId, 'record-groups', recordGroupId],
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: updateForm,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['workspace', currentWorkspaceId, 'record-groups', recordGroupId],
      })
    },
  })

  return {
    form: query.data,
    isLoading: query.isLoading,
    error: query.error,
    create: createMutation.mutate,
    update: updateMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    createError: createMutation.error,
    updateError: updateMutation.error,
  }
}

/** Returns all form versions (basic info only: id, version, recordGroupId). */
export function useAllForms(recordGroupId: string) {
  const { currentWorkspaceId } = useWorkspace()

  return useQuery({
    queryKey: ['workspace', currentWorkspaceId, 'record-groups', recordGroupId, 'all-forms'],
    queryFn: () => getAllForms(recordGroupId),
    enabled: !!recordGroupId && !!currentWorkspaceId,
    staleTime: 60 * 1000,
  })
}

/** Returns a full form (with fields) for a specific formId. */
export function useFormById(formId: string) {
  const { currentWorkspaceId } = useWorkspace()

  return useQuery({
    queryKey: ['workspace', currentWorkspaceId, 'forms', formId],
    queryFn: () => getForm(formId),
    enabled: !!formId && !!currentWorkspaceId,
    staleTime: 60 * 1000,
  })
}

export function useFormTemplates() {
  const { currentWorkspaceId } = useWorkspace()

  return useQuery({
    queryKey: ['workspace', currentWorkspaceId, 'form-templates'],
    queryFn: getFormTemplates,
    enabled: !!currentWorkspaceId,
    staleTime: 5 * 60 * 1000,
  })
}

/** Saving or deleting a template refreshes the list the designer and the new-group page show. */
export function useFormTemplateMutations() {
  const { currentWorkspaceId } = useWorkspace()
  const queryClient = useQueryClient()
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['workspace', currentWorkspaceId, 'form-templates'] })
  return {
    save: useMutation({ mutationFn: createFormTemplate, onSuccess: refresh }),
    remove: useMutation({ mutationFn: deleteFormTemplate, onSuccess: refresh }),
  }
}
